import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { Dialog, useMediaQuery } from '@mui/material';
import { lockDialogPage } from './dialogScrollLock';

const dialogOrigin = () => ({
  element: document.activeElement,
  x: window.scrollX,
  y: window.scrollY,
  route: window.location.pathname + window.location.search,
});

// MUI's default focus restoration also scrolls the opener into view. Keep the
// keyboard focus accessible without panning the album behind touch dialogs.
const StableDialog = React.forwardRef(function StableDialog(
  { open, TransitionProps, PaperProps, ...props },
  forwardedRef,
) {
  const origin = useRef(null);
  const unlock = useRef(null);
  const touchLayout = useMediaQuery('(max-width:1279px)');
  useLayoutEffect(() => {
    if (open && !origin.current) origin.current = dialogOrigin();
    if (open && touchLayout && !unlock.current) unlock.current = lockDialogPage();
    if (!touchLayout && unlock.current) {
      unlock.current();
      unlock.current = null;
    }
  }, [open, touchLayout]);
  if (open && !origin.current) {
    origin.current = dialogOrigin();
  }
  const restore = useCallback(() => {
    unlock.current?.();
    unlock.current = null;
    const saved = origin.current;
    origin.current = null;
    if (!saved || saved.route !== window.location.pathname + window.location.search) return;
    if (saved.element?.isConnected) saved.element.focus?.({ preventScroll: true });
    if (window.scrollX !== saved.x || window.scrollY !== saved.y) {
      window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
    }
  }, []);
  useLayoutEffect(() => () => restore(), [restore]);
  return (
    <Dialog
      {...props}
      open={open}
      disableAutoFocus
      disableRestoreFocus
      disableScrollLock={touchLayout}
      ref={forwardedRef}
      PaperProps={{ tabIndex: -1, ...PaperProps }}
      TransitionProps={{
        ...TransitionProps,
        onEnter: (...args) => {
          const paper = args[0]?.querySelector('[role="dialog"]') || args[0];
          if (!paper?.contains(document.activeElement)) paper?.focus({ preventScroll: true });
          TransitionProps?.onEnter?.(...args);
        },
        onExited: (...args) => {
          restore();
          TransitionProps?.onExited?.(...args);
        },
      }}
    />
  );
});
export default StableDialog;
